import { computed, DestroyRef, effect, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ProductRepositoryService } from '@service/product-repository.service';
import { Product } from '@proto/store/api/store/product';
import { Skus } from '@helper/skus';
import { BaseViewModel } from '../../base-view-model';
import { MetaData } from '@helper/meta-data';

import { isEqual } from 'lodash-es';

@Injectable()
export class SkuTagModel extends BaseViewModel<Product> {
  readonly color = computed(() => this.apiResult().colors.find(c => c.color?.name === this.selectedColor()), {
    equal: (a, b) => isEqual(a, b),
  });
  readonly size = computed(() => this.apiResult().sizes.find(c => c.name === this.selectedSize()), {
    equal: (a, b) => isEqual(a, b),
  });
  readonly availableColors = computed(() => {
    const product = this.apiResult();
    return product.colors.filter(color =>
      product.sizes.some(s => {
        const skuId = Skus.skuId(product.meta?.productId ?? '', color.id, s.id);
        const inventory = product.inventory[skuId];
        if (!inventory) {
          return false;
        }
        return inventory.num > 0;
      })
    );
  });
  readonly availableSizes = computed(
    () => {
      const product = this.apiResult();
      const color = this.color();
      if (!color) {
        return product.sizes;
      }
      return product.sizes.filter(s => {
        const skuId = Skus.skuId(product.meta?.productId ?? '', color.id, s.id);
        const inventory = product.inventory[skuId];
        if (!inventory) {
          return false;
        }
        return inventory.num > 0;
      });
    },
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly hasFullSizes = computed(() => this.availableSizes().length > 4, { equal: (a, b) => isEqual(a, b) });
  // color name
  readonly selectedColor = signal('');
  // size name
  readonly selectedSize = signal('');

  constructor(private lativRepository: ProductRepositoryService) {
    super(Product.fromPartial({}));
    effect(() => {
      // make sure current selectedSize is valid
      const available = this.availableSizes();
      if (!available.some(s => s.name === this.selectedSize())) {
        this.selectedSize.set(available[0]?.name ?? '');
      }
    });
  }

  getProduct(ref: DestroyRef, productId: string): void {
    this.lativRepository
      .getProduct({
        productId,
        preview: false,
      })
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        next: product => {
          this.setApiResult(product);
          if (!this.selectedColor().length) {
            this.selectedColor.set(product.colors[0]?.color?.name ?? '');
          }
          if (!this.selectedSize().length) {
            this.selectedSize.set(this.availableSizes()[0]?.name ?? '');
          }
        },
        error: () => {
          // ignore error
        },
      });
  }

  override getMetaByUrl(): MetaData | undefined {
    return undefined;
  }
}
